import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { api, getToken, setToken, clearToken, ApiError, NetworkError, setUnauthorizedHandler } from "./api";
import { normalizeSessionUser, type SessionUser } from "./authNormalize";
import { setSentryUser } from "./sentry";

type User = SessionUser;

interface AuthState {
    user: User | null;
    loading: boolean;
    error: string | null;
    /**
     * We hold a token but could not reach the server to check it. The session
     * is NOT known to be gone — we simply could not ask. Kept separate from
     * `user: null` so the app can offer "try again" instead of pretending the
     * tech is signed out (or, worse, spinning forever).
     */
    unreachable: boolean;
}

interface AuthContextValue extends AuthState {
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle: (idToken: string) => Promise<void>;
    logout: () => void;
    /** Admin-only: assume another user's session for testing. */
    impersonate: (userId: string) => Promise<void>;
    /** Return from an impersonated session back to the admin. */
    stopImpersonating: () => Promise<void>;
    /** When set, the current session is an admin acting as another user. */
    actingAs: { actorUserId: string } | null;
    isOffice: boolean;
    isField: boolean;
    isAdmin: boolean;
    /** PhotoImporter is a locked-down temp role that may only see the
     *  ServiceTitan photo-import screen. Routing in main.tsx relies on this. */
    isPhotoImporter: boolean;
    /** True when the user is paid hourly (office day-clock). Drives the
     *  self-service punch control + the hard clock-in gate. */
    isHourly: boolean;
    /** Capability check. Admins implicitly pass everything. */
    can: (permissionKey: string) => boolean;
    /** Re-check a session that `unreachable` left unverified. */
    retryAuth: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        user: null,
        loading: !!getToken(),
        error: null,
        unreachable: false,
    });

    const fetchMe = useCallback(async () => {
        try {
            const data = await api.get<{ user: Record<string, unknown> }>("/api/auth/me");
            const user = normalizeSessionUser(data.user);
            if (!user) {
                clearToken();
                setState({ user: null, loading: false, error: null, unreachable: false });
                setSentryUser(null);
                return;
            }
            setState({ user, loading: false, error: null, unreachable: false });
            setSentryUser({ id: user.id, email: user.email, roleName: user.role });
        } catch (err) {
            // Could not REACH the server (timeout / dropped link). We do not
            // know whether the session is still good, so keep the token and
            // say plainly that we could not check. Two things this must never
            // do, both of which it used to: clear the token (logging a tech
            // out for driving through a dead spot) or leave `loading` true
            // (the spinner with no error, no retry and no way out — #1360).
            if (err instanceof NetworkError) {
                setState({ user: null, loading: false, error: null, unreachable: true });
                return;
            }
            clearToken();
            setState({ user: null, loading: false, error: null, unreachable: false });
            setSentryUser(null);
            if (err instanceof ApiError && err.status === 401) return;
            console.error("Auth check failed:", err);
        }
    }, []);

    /** Re-check a session we couldn't verify. Drives the "Try again" button on
     *  the unreachable screen; a no-op once the token is gone. */
    const retryAuth = useCallback(() => {
        if (!getToken()) return;
        setState((s) => ({ ...s, loading: true, unreachable: false }));
        void fetchMe();
    }, [fetchMe]);

    useEffect(() => {
        if (getToken()) fetchMe();
    }, [fetchMe]);

    /* A 401 anywhere means the session is gone. Drop it here rather than
     * letting each of 1,452 queries decide for itself — RequireAuth then sends
     * the user to /login, which explains why and returns them afterwards.
     * `api.ts` holds the return path; it cannot import this module (we import
     * it), hence the registered handler. See threads/cycle-1-believable/ §D4. */
    useEffect(
        () =>
            setUnauthorizedHandler(() => {
                clearToken();
                setState({ user: null, loading: false, error: null, unreachable: false });
                setSentryUser(null);
            }),
        [],
    );

    const login = async (email: string, password: string) => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const data = await api.post<{ token: string; user: Record<string, unknown> }>("/api/auth/login", {
                email,
                password,
            });
            setToken(data.token);
            const user = normalizeSessionUser(data.user);
            if (!user) {
                clearToken();
                setState({ user: null, loading: false, error: "Invalid login response", unreachable: false });
                setSentryUser(null);
                throw new Error("INVALID_LOGIN_USER");
            }
            setState({ user, loading: false, error: null, unreachable: false });
            setSentryUser({ id: user.id, email: user.email, roleName: user.role });
            // The login payload omits capabilities; hydrate them from /me so
            // can(...) works immediately without a manual refresh.
            void fetchMe();
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? (err.body as { error?: string })?.error || "Invalid credentials"
                    : "Network error";
            setState({ user: null, loading: false, error: msg, unreachable: false });
            throw err;
        }
    };

    const loginWithGoogle = async (idToken: string) => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const data = await api.post<{ token: string; user: Record<string, unknown> }>("/api/auth/google", {
                idToken,
            });
            setToken(data.token);
            await fetchMe();
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? (err.body as { error?: string })?.error || "Google sign-in failed"
                    : "Network error";
            setState({ user: null, loading: false, error: msg, unreachable: false });
            throw err;
        }
    };

    const impersonate = async (userId: string) => {
        const data = await api.post<{ token: string }>(`/api/admin/impersonate/${userId}`, {});
        setToken(data.token);
        await fetchMe();
    };

    const stopImpersonating = async () => {
        const data = await api.post<{ token: string }>("/api/auth/stop-impersonating", {});
        setToken(data.token);
        await fetchMe();
    };

    const logout = () => {
        clearToken();
        setState({ user: null, loading: false, error: null, unreachable: false });
        setSentryUser(null);
    };

    const role = state.user?.role?.toLowerCase() ?? "";
    const isAdmin = role === "admin";
    const permissions = state.user?.permissions ?? [];
    const can = useCallback(
        (permissionKey: string) => isAdmin || permissions.includes(permissionKey),
        [isAdmin, permissions],
    );
    const value: AuthContextValue = {
        ...state,
        login,
        loginWithGoogle,
        logout,
        impersonate,
        stopImpersonating,
        actingAs: state.user?.actingAs ?? null,
        // Office-family (office nav, home, help drawer). "Front Desk" is the
        // new low-trust office rung — it runs the day but sees no money.
        isOffice: role === "office" || role === "front desk" || role === "manager" || role === "admin",
        // Field-family (field app, field home). "Lead Tech" is a senior tech.
        isField: role === "field" || role === "technician" || role === "lead tech",
        isAdmin,
        isPhotoImporter: role === "photoimporter" || role === "photo_importer",
        isHourly: state.user?.pay_type === "hourly",
        can,
        retryAuth,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
